import { useEffect, useRef, useState } from 'react';
import api, { TOKEN_STORAGE_KEY } from '../api/client';

export default function useUnitHeartbeat(user, gps, division, { isMobile = true, intervalMs = 15000 } = {}) {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  const owner = user?.id && user?.rangerId && token ? JSON.stringify([user.id, user.rangerId, token]) : null;
  const latest = useRef({ owner, gps, division, isMobile });
  latest.current = { owner, gps, division, isMobile };
  const [state, setState] = useState({ owner: null, error: '' });
  useEffect(() => {
    if (!owner) return undefined;
    let cancelled = false, timer;
    const abort = new AbortController();
    const current = () => !cancelled && latest.current.owner === owner && localStorage.getItem(TOKEN_STORAGE_KEY) === token;
    const send = async () => {
      if (!current()) return;
      const position = latest.current.gps;
      // This allowlist deliberately has no cached dispatch/status fields.
      const payload = { isMobile: latest.current.isMobile, latitude: position?.lat, longitude: position?.lng,
        heading: position?.heading, speed: position?.speed, division: latest.current.division || undefined };
      try {
        const response = await api.post('/status/heartbeat', payload,
          { timeout: 15000, signal: abort.signal, expectedSessionToken: token });
        if (response?.status !== 200 || !Number.isFinite(Date.parse(response.data?.serverTimeUtc)))
          throw new Error('Heartbeat confirmation missing');
        if (current()) setState({ owner, error: '' });
      } catch {
        if (current()) setState({ owner, error: 'Your location/check-in update was not confirmed. Retrying when connected.' });
      } finally {
        // Schedule after completion: a slow request cannot overlap the next one.
        if (current()) timer = setTimeout(send, Math.min(600000, Math.max(5000, Number(intervalMs) || 15000)));
      }
    };
    send();
    return () => { cancelled = true; abort.abort(); clearTimeout(timer); };
  }, [owner, token, intervalMs]);
  return state.owner === owner ? state.error : '';
}
